import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '../App';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { Mail, CheckCircle, XCircle, Clock, Users, FileText, Calendar, History, Trash2, RefreshCw } from 'lucide-react';
import { format } from 'date-fns';

export default function EmailUploads({ user }) {
  const [pendingUploads, setPendingUploads] = useState([]);
  const [approvedUploads, setApprovedUploads] = useState([]);
  const [emailHistory, setEmailHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('pending');
  const [processingId, setProcessingId] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [pendingRes, approvedRes, historyRes] = await Promise.all([
        axios.get(`${API}/admin/pending-emails`),
        axios.get(`${API}/admin/approved-emails`),
        axios.get(`${API}/admin/email-history`)
      ]);
      setPendingUploads(pendingRes.data);
      setApprovedUploads(approvedRes.data);
      setEmailHistory(historyRes.data);
    } catch (error) {
      toast.error('Failed to load email data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleApprove = async (uploadId) => {
    setProcessingId(uploadId);
    try {
      await axios.post(`${API}/admin/pending-emails/${uploadId}/approve`);
      toast.success('Upload approved! Scorecard created.');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve upload');
    } finally {
      setProcessingId(null);
    }
  };

  const handleReject = async (uploadId) => {
    if (!window.confirm('Are you sure you want to reject this upload? The scorecard will not be created.')) {
      return;
    }
    
    setProcessingId(uploadId);
    try {
      await axios.post(`${API}/admin/pending-emails/${uploadId}/reject`);
      toast.success('Upload rejected');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject upload');
    } finally {
      setProcessingId(null);
    }
  };

  const handleDelete = async (uploadId) => {
    if (!window.confirm('Are you sure you want to permanently delete this upload? This cannot be undone.')) {
      return;
    }
    
    setProcessingId(uploadId);
    try {
      await axios.delete(`${API}/admin/pending-emails/${uploadId}`);
      toast.success('Upload deleted permanently');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete upload');
    } finally {
      setProcessingId(null);
    }
  };

  const handleDeleteHistory = async (messageId, hasScorecard) => {
    const confirmMsg = hasScorecard 
      ? 'This will delete both the email record AND the associated scorecard (removing points from standings). Continue?'
      : 'Delete this email history entry?';
    
    if (!window.confirm(confirmMsg)) {
      return;
    }
    
    setProcessingId(messageId);
    try {
      await axios.delete(`${API}/admin/email-history/${messageId}?delete_scorecard=true`);
      toast.success(hasScorecard ? 'Email and scorecard deleted' : 'Email history deleted');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
    } finally {
      setProcessingId(null);
    }
  };

  const handleDeleteApproved = async (uploadId, scorecardExists) => {
    const confirmMsg = scorecardExists 
      ? 'This will delete the approved upload AND its scorecard (removing points from standings). Continue?'
      : 'Delete this approved upload record?';
    
    if (!window.confirm(confirmMsg)) {
      return;
    }
    
    setProcessingId(uploadId);
    try {
      await axios.delete(`${API}/admin/pending-emails/${uploadId}?delete_scorecard=true`);
      toast.success(scorecardExists ? 'Upload and scorecard deleted' : 'Approved upload deleted');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
    } finally {
      setProcessingId(null);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'uploaded':
        return <span className="inline-flex items-center gap-1 bg-green-100 text-green-700 px-2 py-1 rounded text-xs font-semibold"><CheckCircle className="w-3 h-3" /> Uploaded</span>;
      case 'pending_approval':
        return <span className="inline-flex items-center gap-1 bg-amber-100 text-amber-700 px-2 py-1 rounded text-xs font-semibold"><Clock className="w-3 h-3" /> Pending</span>;
      case 'failed':
        return <span className="inline-flex items-center gap-1 bg-red-100 text-red-700 px-2 py-1 rounded text-xs font-semibold"><XCircle className="w-3 h-3" /> Failed</span>;
      case 'skipped':
        return <span className="inline-flex items-center gap-1 bg-stone-100 text-stone-600 px-2 py-1 rounded text-xs font-semibold">Skipped</span>;
      case 'no_attachments':
        return <span className="inline-flex items-center gap-1 bg-stone-100 text-stone-600 px-2 py-1 rounded text-xs font-semibold">No Files</span>;
      default:
        return <span className="inline-flex items-center gap-1 bg-stone-100 text-stone-600 px-2 py-1 rounded text-xs font-semibold">{status}</span>;
    }
  };

  const handleReprocessEmails = async () => {
    if (!window.confirm('This will clear all processed email records and reprocess all emails. Any existing scorecards from emails will NOT be deleted. Continue?')) {
      return;
    }
    
    try {
      const response = await axios.post(`${API}/admin/reprocess-emails`);
      toast.success(response.data.message);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reprocess emails');
    }
  };

  if (loading) {
    return (
      <Layout user={user}>
        <div className="flex items-center justify-center min-h-screen">
          <p className="font-barlow text-xl text-slate-900">Loading...</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user}>
      <div className="max-w-7xl mx-auto p-4 md:p-8 lg:p-12">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-3">
              <Mail className="w-8 h-8 text-amber-600" strokeWidth={1.5} />
              <h1 className="font-barlow font-bold text-4xl md:text-5xl text-slate-900 uppercase tracking-tight">
                Email Uploads
              </h1>
            </div>
            <Button
              onClick={handleReprocessEmails}
              variant="outline"
              data-testid="reprocess-emails-btn"
              className="border-amber-300 text-amber-600 hover:bg-amber-50 rounded-sm font-barlow font-semibold uppercase tracking-wide text-sm"
            >
              <RefreshCw className="w-4 h-4 mr-2" strokeWidth={1.5} />
              Reprocess All
            </Button>
          </div>
          <p className="font-inter text-stone-600">
            Manage scorecards sent via email to scores@lospistoleros.co.za
          </p>
        </div>

        {/* Info Box */}
        <div className="bg-amber-50 border border-amber-200 rounded-sm p-4 mb-6">
          <p className="font-inter text-sm text-amber-800">
            <strong>Auto-upload:</strong> Emails from registered users are automatically processed.
            <br />
            <strong>Manual approval:</strong> Emails from unregistered senders appear below for admin review.
          </p>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-stone-200 overflow-x-auto">
          <button
            onClick={() => setActiveTab('pending')}
            data-testid="pending-tab"
            className={`font-barlow font-semibold uppercase tracking-wide text-sm px-6 py-3 whitespace-nowrap ${
              activeTab === 'pending'
                ? 'border-b-2 border-amber-600 text-amber-600'
                : 'text-stone-600 hover:text-slate-900'
            }`}
          >
            <Clock className="w-4 h-4 inline mr-2" />
            Pending ({pendingUploads.length})
          </button>
          <button
            onClick={() => setActiveTab('approved')}
            data-testid="approved-tab"
            className={`font-barlow font-semibold uppercase tracking-wide text-sm px-6 py-3 whitespace-nowrap ${
              activeTab === 'approved'
                ? 'border-b-2 border-green-600 text-green-600'
                : 'text-stone-600 hover:text-slate-900'
            }`}
          >
            <CheckCircle className="w-4 h-4 inline mr-2" />
            Approved ({approvedUploads.length})
          </button>
          <button
            onClick={() => setActiveTab('history')}
            data-testid="history-tab"
            className={`font-barlow font-semibold uppercase tracking-wide text-sm px-6 py-3 whitespace-nowrap ${
              activeTab === 'history'
                ? 'border-b-2 border-amber-600 text-amber-600'
                : 'text-stone-600 hover:text-slate-900'
            }`}
          >
            <History className="w-4 h-4 inline mr-2" />
            Email History
          </button>
        </div>

        {/* Pending Uploads Tab */}
        {activeTab === 'pending' && (
          <div>
            {pendingUploads.length === 0 ? (
              <div className="bg-white border border-stone-200 rounded-sm p-12 text-center">
                <Mail className="w-12 h-12 text-stone-300 mx-auto mb-4" strokeWidth={1.5} />
                <p className="font-inter text-stone-500">No pending uploads to review</p>
              </div>
            ) : (
              <div className="space-y-4">
                {pendingUploads.map((upload) => (
                  <div 
                    key={upload.id} 
                    className="bg-white border border-stone-200 rounded-sm p-6 shadow-sm"
                    data-testid={`pending-upload-${upload.id}`}
                  >
                    <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                      {/* Left side - Info */}
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-3">
                          <FileText className="w-5 h-5 text-amber-600" strokeWidth={1.5} />
                          <h3 className="font-barlow font-bold text-lg text-slate-900">{upload.filename}</h3>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm font-inter">
                          <div className="flex items-center gap-2 text-stone-600">
                            <Mail className="w-4 h-4" strokeWidth={1.5} />
                            <span className="font-medium">From:</span> {upload.sender_email}
                          </div>
                          
                          {upload.scorecard_date && (
                            <div className="flex items-center gap-2 text-stone-600">
                              <Calendar className="w-4 h-4" strokeWidth={1.5} />
                              <span className="font-medium">Competition Date:</span> {upload.scorecard_date}
                            </div>
                          )}
                          
                          <div className="flex items-center gap-2 text-stone-600">
                            <Users className="w-4 h-4" strokeWidth={1.5} />
                            <span className="font-medium">Players:</span> {upload.player_count}
                          </div>
                          
                          <div className="flex items-center gap-2 text-stone-600">
                            <Clock className="w-4 h-4" strokeWidth={1.5} />
                            <span className="font-medium">Received:</span> {format(new Date(upload.created_at), 'MMM dd, yyyy HH:mm')}
                          </div>
                        </div>

                        {/* Player Preview */}
                        {upload.players && upload.players.length > 0 && (
                          <div className="mt-4 pt-4 border-t border-stone-100">
                            <p className="font-barlow font-semibold text-xs uppercase tracking-wider text-stone-500 mb-2">Players Preview</p>
                            <div className="flex flex-wrap gap-2">
                              {upload.players.slice(0, 5).map((player) => (
                                <span key={`${player.name}-${player.division}`} className="bg-stone-100 text-stone-700 text-xs px-2 py-1 rounded font-inter">
                                  {player.name} ({player.division}: {player.score})
                                </span>
                              ))}
                              {upload.players.length > 5 && (
                                <span className="text-stone-500 text-xs px-2 py-1 font-inter">
                                  +{upload.players.length - 5} more
                                </span>
                              )}
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Right side - Actions */}
                      <div className="flex gap-2 lg:flex-col">
                        <Button
                          onClick={() => handleApprove(upload.id)}
                          disabled={processingId === upload.id}
                          data-testid={`approve-btn-${upload.id}`}
                          className="bg-green-600 text-white hover:bg-green-700 rounded-sm font-barlow font-semibold uppercase tracking-wide text-sm px-4"
                        >
                          <CheckCircle className="w-4 h-4 mr-2" strokeWidth={1.5} />
                          {processingId === upload.id ? 'Processing...' : 'Approve'}
                        </Button>
                        <Button
                          onClick={() => handleReject(upload.id)}
                          disabled={processingId === upload.id}
                          data-testid={`reject-btn-${upload.id}`}
                          variant="outline"
                          className="border-amber-300 text-amber-600 hover:bg-amber-50 rounded-sm font-barlow font-semibold uppercase tracking-wide text-sm px-4"
                        >
                          <XCircle className="w-4 h-4 mr-2" strokeWidth={1.5} />
                          Reject
                        </Button>
                        <Button
                          onClick={() => handleDelete(upload.id)}
                          disabled={processingId === upload.id}
                          data-testid={`delete-btn-${upload.id}`}
                          variant="outline"
                          className="border-red-300 text-red-600 hover:bg-red-50 rounded-sm font-barlow font-semibold uppercase tracking-wide text-sm px-4"
                        >
                          <Trash2 className="w-4 h-4 mr-2" strokeWidth={1.5} />
                          Delete
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Approved Uploads Tab */}
        {activeTab === 'approved' && (
          <div className="bg-white border border-stone-200 rounded-sm shadow-sm overflow-hidden">
            {approvedUploads.length === 0 ? (
              <div className="p-12 text-center">
                <CheckCircle className="w-12 h-12 text-stone-300 mx-auto mb-4" strokeWidth={1.5} />
                <p className="font-inter text-stone-500">No approved uploads yet</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-stone-100 border-b border-stone-200">
                    <tr>
                      <th className="text-left py-3 px-4 font-barlow font-bold text-xs uppercase tracking-wider text-stone-600">Sender</th>
                      <th className="text-left py-3 px-4 font-barlow font-bold text-xs uppercase tracking-wider text-stone-600">File</th>
                      <th className="text-left py-3 px-4 font-barlow font-bold text-xs uppercase tracking-wider text-stone-600">Date</th>
                      <th className="text-center py-3 px-4 font-barlow font-bold text-xs uppercase tracking-wider text-stone-600">Players</th>
                      <th className="text-center py-3 px-4 font-barlow font-bold text-xs uppercase tracking-wider text-stone-600">Scorecard</th>
                      <th className="text-center py-3 px-4 font-barlow font-bold text-xs uppercase tracking-wider text-stone-600">Approved</th>
                      <th className="text-center py-3 px-4 font-barlow font-bold text-xs uppercase tracking-wider text-stone-600">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {approvedUploads.map((upload) => (
                      <tr key={upload.id} className="border-b border-stone-100 hover:bg-stone-50">
                        <td className="py-4 px-4 font-inter text-sm text-slate-900">{upload.sender_email}</td>
                        <td className="py-4 px-4 font-inter text-sm text-stone-600">{upload.filename}</td>
                        <td className="py-4 px-4 font-inter text-sm text-stone-600">{upload.scorecard_date || '-'}</td>
                        <td className="py-4 px-4 text-center font-inter text-sm text-stone-600">{upload.player_count}</td>
                        <td className="py-4 px-4 text-center">
                          {upload.scorecard_exists ? (
                            <span className="inline-flex items-center gap-1 bg-green-100 text-green-700 px-2 py-1 rounded text-xs font-semibold">
                              <CheckCircle className="w-3 h-3" /> Active
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 bg-stone-100 text-stone-500 px-2 py-1 rounded text-xs font-semibold">
                              Deleted
                            </span>
                          )}
                        </td>
                        <td className="py-4 px-4 text-center font-inter text-sm text-stone-600">
                          {upload.approved_at ? format(new Date(upload.approved_at), 'MMM dd, yyyy HH:mm') : '-'}
                        </td>
                        <td className="py-4 px-4 text-center">
                          <Button
                            onClick={() => handleDeleteApproved(upload.id, upload.scorecard_exists)}
                            disabled={processingId === upload.id}
                            data-testid={`delete-approved-${upload.id}`}
                            variant="ghost"
                            size="sm"
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            title={upload.scorecard_exists ? "Delete upload and scorecard" : "Delete upload record"}
                          >
                            <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Email History Tab */}
        {activeTab === 'history' && (
          <div className="bg-white border border-stone-200 rounded-sm shadow-sm overflow-hidden">
            {emailHistory.length === 0 ? (
              <div className="p-12 text-center">
                <History className="w-12 h-12 text-stone-300 mx-auto mb-4" strokeWidth={1.5} />
                <p className="font-inter text-stone-500">No email history yet</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-stone-100 border-b border-stone-200">
                    <tr>
                      <th className="text-left py-3 px-4 font-barlow font-bold text-xs uppercase tracking-wider text-stone-600">Sender</th>
                      <th className="text-center py-3 px-4 font-barlow font-bold text-xs uppercase tracking-wider text-stone-600">Status</th>
                      <th className="text-left py-3 px-4 font-barlow font-bold text-xs uppercase tracking-wider text-stone-600">Date</th>
                      <th className="text-left py-3 px-4 font-barlow font-bold text-xs uppercase tracking-wider text-stone-600">Details</th>
                      <th className="text-center py-3 px-4 font-barlow font-bold text-xs uppercase tracking-wider text-stone-600">Processed</th>
                      <th className="text-center py-3 px-4 font-barlow font-bold text-xs uppercase tracking-wider text-stone-600">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {emailHistory.map((email) => (
                      <tr key={email.message_id} className="border-b border-stone-100 hover:bg-stone-50">
                        <td className="py-4 px-4 font-inter text-sm text-slate-900">{email.sender}</td>
                        <td className="py-4 px-4 text-center">{getStatusBadge(email.status)}</td>
                        <td className="py-4 px-4 font-inter text-sm text-stone-600">{email.scorecard_date || '-'}</td>
                        <td className="py-4 px-4 font-inter text-sm text-stone-600 max-w-xs truncate">{email.details || '-'}</td>
                        <td className="py-4 px-4 text-center font-inter text-sm text-stone-600">
                          {format(new Date(email.processed_at), 'MMM dd, yyyy HH:mm')}
                        </td>
                        <td className="py-4 px-4 text-center">
                          <Button
                            onClick={() => handleDeleteHistory(email.message_id, !!email.scorecard_id)}
                            disabled={processingId === email.message_id}
                            data-testid={`delete-history-${email.message_id}`}
                            variant="ghost"
                            size="sm"
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
}
